import React, { forwardRef } from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';

import { useModals, ModalNames } from 'components/modals';
import { useCheckAllAddressFlags } from 'shared/actions/address';
import { mediaSizes } from 'theme';
import { ORDER_TYPE_PICKUP, ORDER_TYPE_DELIVERY } from 'shared/constants';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import {
  afterHoursEnabledForDelivery,
  afterHoursEnabledForDeliveryV2,
  afterHoursEnabledForPickup,
  afterHoursEnabledForPickupV2,
  scheduledOrderingEnabledForDelivery,
  scheduledOrderingEnabledForDeliveryV2,
  shouldHideOrderEstimates,
  openInfoForPickupV2,
  getFirstAvailablePickupOption,
} from 'shared/helpers/dispensaries';
import { openInfoForDispensary, openInfoForDispensaryV2 } from 'shared/core/helpers/dispensaries';
import { ModalContainer, ModalExitIcon } from 'components/core';
import { bold } from 'components/core/typography';
import { formatEstimate } from 'shared/helpers/order-estimates';
import { PickupButton, DeliveryButton } from './order-type-buttons';

export const OrderTypeModal = forwardRef(({ onClose, containerProps }, ref) => {
  const { t } = useTranslation();
  const { dispensary } = useDispensary();
  const UI = useUI();
  const { openModal } = useModals();
  const Cart = useCart();
  const { durationEstimates, hasDeliveryAddress } = Cart;
  const { previewMode, timezone } = UI;
  const useOrderTypesConfigV2 = useDispensaryFlag(`rollout.use-updated-ordering-configurations`, dispensary.id);
  const checkoutAllAddressFlags = useCheckAllAddressFlags();

  let pickup;
  let delivery;
  let openInfo;
  if (useOrderTypesConfigV2) {
    openInfo = openInfoForDispensaryV2(dispensary, {
      userTimezone: timezone,
      previewMode,
    });

    const { info } = openInfoForPickupV2(openInfo);
    pickup = info;
    delivery = openInfo.delivery;
  } else {
    openInfo = openInfoForDispensary(dispensary, {
      userTimezone: timezone,
      previewMode,
    });

    pickup = openInfo.pickup;
    delivery = openInfo.delivery;
  }

  const hideOrderEstimates = shouldHideOrderEstimates(dispensary, true);
  const { pickup: pickupEstimate, delivery: deliveryEstimate } = durationEstimates;

  const formattedPickupEstimate = !hideOrderEstimates && formatEstimate(pickupEstimate, ORDER_TYPE_PICKUP);
  const formattedDeliveryEstimate = !hideOrderEstimates && formatEstimate(deliveryEstimate, ORDER_TYPE_DELIVERY);

  const offersAfterHoursDelivery = useOrderTypesConfigV2
    ? afterHoursEnabledForDeliveryV2(dispensary) && scheduledOrderingEnabledForDeliveryV2(dispensary)
    : afterHoursEnabledForDelivery(dispensary) && scheduledOrderingEnabledForDelivery(dispensary);

  const offersAfterHoursPickup = useOrderTypesConfigV2
    ? afterHoursEnabledForPickupV2(dispensary)
    : afterHoursEnabledForPickup(dispensary);

  const handleChooseOrderType = async (type) => {
    if (type === ORDER_TYPE_PICKUP) {
      Cart.setOrderType(getFirstAvailablePickupOption(dispensary));
    } else {
      Cart.setOrderType(type);

      const goodAddress = await checkoutAllAddressFlags();

      if (!goodAddress) {
        await openModal(ModalNames.deliveryAddress);
      }
    }

    return onClose();
  };

  const handleCloseWithoutChoosing = () => {
    const firstAvailableOrderType = dispensary.offerAnyPickupService
      ? getFirstAvailablePickupOption(dispensary)
      : ORDER_TYPE_DELIVERY;

    Cart.setOrderType(firstAvailableOrderType);
    onClose();
  };

  return (
    <ModalContainer ref={ref} height={327} width={502} {...containerProps}>
      <Header>{t('orderType.selectOrderType', 'select an order type')}</Header>
      <ModalExitIcon onClick={handleCloseWithoutChoosing} />

      <ButtonContainer>
        <PickupButton
          tabindex='0'
          mb={17}
          pickupInfo={pickup}
          dispensary={dispensary}
          formattedPickupEstimate={formattedPickupEstimate}
          offersAfterHours={offersAfterHoursPickup}
          onClick={() => {
            if (pickup.isClosed && !offersAfterHoursPickup) {
              return;
            }
            handleChooseOrderType(ORDER_TYPE_PICKUP);
          }}
        />
        <DeliveryButton
          deliveryInfo={delivery}
          dispensary={dispensary}
          formattedDeliveryEstimate={formattedDeliveryEstimate}
          hasDeliveryAddress={hasDeliveryAddress()}
          offersAfterHours={offersAfterHoursDelivery}
          onClick={() => {
            if (delivery.isClosed && !offersAfterHoursDelivery) {
              return;
            }
            handleChooseOrderType(ORDER_TYPE_DELIVERY);
          }}
        />
      </ButtonContainer>
    </ModalContainer>
  );
});

const Header = styled.h3`
  margin-top: 8px;
  margin-bottom: 25px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[35]};
  ${bold};
  text-align: center;
  text-transform: uppercase;
`;

const ButtonContainer = styled.div`
  padding: 0 26px 13px 26px;

  @media (max-width: ${mediaSizes.phone}px) {
    padding: 0;
  }
`;
